import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { addBOM } from "../../store/slices/bomSlice";
import { fetchProducts } from "../../store/slices/productSlice";
import { fetchDropdownData } from "../../store/slices/storeInwardSlice";
import toast from "react-hot-toast";
import Button, { ButtonType } from "../../components/Assets/Button";
import Select from "react-select";
import { UNITS } from "../../models/warehouseRawMaterial.model";
import {
  getUnitsByType,
  getDefaultUnitForType,
} from "../../utils/warehouse.util";
import { usePreventWheelChange } from "../../utils/usePreventWheelChange";

interface CreateBOMProps {
  onSuccess: () => void;
  onCancel: () => void;
  isEdit: boolean;
  via?: "bom" | "priceList";
}

const CreateBOM: React.FC<CreateBOMProps> = ({
  onSuccess,
  onCancel,
  isEdit,
  via,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const { products } = useSelector((state: RootState) => state.product);
  const { rawMaterials } = useSelector((state: RootState) => state.storeInward);
  const { selectedBOM: bom, createBOMLoading } = useSelector(
    (state: RootState) => state.billOfMaterials,
  );

  const [formData, setFormData] = useState({ name: "", description: "" });
  const [noOfPanels, setNoOfPanels] = useState<number | "">("");
  const [selectedItems, setSelectedItems] = useState<any[]>([]);

  const [productPick, setProductPick] = useState({ id: "", quantity: 1 });
  const [materialPick, setMaterialPick] = useState({
    id: "",
    quantity: 1,
    unit: "",
  });
  const handleWheelPrevent = usePreventWheelChange();
  useEffect(() => {
    dispatch(fetchProducts()).then(() => {
      if (bom && isEdit) {
        const productItems = bom.items.filter(
          (item) => item.type === "product",
        );
        const materialItems = bom.items.filter(
          (item) => item.type === "material",
        );
        setSelectedItems([
          ...productItems.map((item) => ({
            type: "product",
            id: item.product._id,
            quantity: item.quantity,
            name: item.product.productName,
          })),
          ...materialItems.map((item) => ({
            type: "material",
            id: item.rawMaterial?._id,
            quantity: item.quantity,
            unitLabel: item?.displayUnit,
            name: item.rawMaterial?.name,
            unitValue: item?.displayUnit,
          })),
        ]);
      }
    });

    dispatch(fetchDropdownData());
    if (bom && isEdit) {
      setFormData({
        name: bom.name,
        description: bom.description || "",
      });
    }
  }, [dispatch, bom, isEdit]);

  const addProduct = () => {
    if (!productPick.id) return toast.error("Select a product");
    const prod = products.find((p) => p._id === productPick.id);
    if (!prod) return;

    const existingIndex = selectedItems.findIndex(
      (item) => item.type === "product" && item.id === prod._id,
    );

    if (existingIndex !== -1) {
      const updated = [...selectedItems];
      updated[existingIndex].quantity += productPick.quantity;
      setSelectedItems(updated);
    } else {
      setSelectedItems((prev) => [
        ...prev,
        {
          type: "product",
          id: prod._id,
          name: prod.productName,
          quantity: productPick.quantity,
          data: prod,
        },
      ]);
    }
    setProductPick({ id: "", quantity: 1 });
  };

  const addMaterial = () => {
    if (!materialPick.id) return toast.error("Select a material");
    if (materialPick.quantity < 1) return toast.error("Min quantity is 1");
    if (!materialPick.unit) return toast.error("Select a unit");

    const mat = rawMaterials.find((m) => m._id === materialPick.id);
    if (!mat) return;

    const unitLabel =
      UNITS.find((u) => u.value === materialPick.unit)?.label ||
      materialPick.unit;

    const existingIndex = selectedItems.findIndex(
      (item) =>
        item.type === "material" &&
        item.id === mat._id &&
        item.unitValue === materialPick.unit,
    );

    if (existingIndex !== -1) {
      const updated = [...selectedItems];
      updated[existingIndex].quantity += materialPick.quantity;
      setSelectedItems(updated);
    } else {
      setSelectedItems((prev) => [
        ...prev,
        {
          type: "material",
          id: mat._id,
          name: mat.name,
          quantity: materialPick.quantity,
          unitValue: materialPick.unit,
          unitLabel: unitLabel,
          data: mat,
        },
      ]);
    }
    setMaterialPick({ id: "", quantity: 1, unit: "" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return toast.error("BOM Name is required");
    if (selectedItems.length === 0) return toast.error("Add at least one item");
    if (via === "priceList" && (!noOfPanels || noOfPanels <= 0)) {
      return toast.error("No of panels is required");
    }

    const payload = {
      ...formData,
      noOfPanels,
      via: via,
      items: selectedItems.map((item) => ({
        productId: item.id,
        type: item.type,
        quantity: item.quantity,
        unit: item.type === "material" ? item.unitValue : undefined,
      })),
    };
    try {
      await dispatch(addBOM(payload)).unwrap();
      onSuccess();
    } catch (error) {
      toast.error("Failed to create BOM");
    }
  };

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: "42px",
      fontSize: "0.9rem",
      borderRadius: "0.5rem",
      borderColor: "#D1D5DB",
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: "0 12px",
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="relative bg-white rounded-xl p-6 min-h-[750px] flex flex-col">
      {createBOMLoading && (
        <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-50 flex items-center justify-center rounded-xl">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm font-semibold text-gray-600">Saving BOM...</p>
          </div>
        </div>
      )}
      <div className="bg-white rounded-xl p-6 min-h-[750px] flex flex-col">
        <div className="space-y-6 flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <input
              type="text"
              placeholder="BOM Name *"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg h-[42px] focus:ring-2 focus:ring-blue-500 outline-none"
            />
            <input
              type="text"
              placeholder="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg h-[42px] focus:ring-2 focus:ring-blue-500 outline-none"
            />
            <input
              type="number"
              placeholder="No of Panels *"
              value={noOfPanels}
              onChange={(e) => setNoOfPanels(Number(e.target.value))}
              className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg h-[42px] focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>

          <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100">
            <label className="block text-[10px] font-bold text-blue-800 mb-3 uppercase tracking-widest">
              1. Add Products
            </label>
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1 w-full">
                <Select
                  options={products.map((p) => ({
                    value: p._id,
                    label: p.productName,
                  }))}
                  onChange={(opt: any) =>
                    setProductPick((p) => ({ ...p, id: opt?.value || "" }))
                  }
                  value={
                    products.find((p) => p._id === productPick.id)
                      ? {
                          value: productPick.id,
                          label: products.find((p) => p._id === productPick.id)
                            ?.productName,
                        }
                      : null
                  }
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  placeholder="Select Product"
                />
              </div>
              <div className="w-full md:w-24">
                <input
                  type="number"
                  min={1}
                  onWheel={handleWheelPrevent}
                  value={productPick.quantity}
                  onChange={(e) =>
                    setProductPick((p) => ({ ...p, quantity: +e.target.value }))
                  }
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg h-[42px] outline-none"
                />
              </div>
              <Button
                type={ButtonType.Submit}
                onClick={addProduct}
                className="text-xs h-[42px] px-8 w-full md:w-auto"
              >
                Add
              </Button>
            </div>
          </div>

          <div className="p-4 bg-green-50/50 rounded-xl border border-green-100">
            <label className="block text-[10px] font-bold text-green-800 mb-3 uppercase tracking-widest">
              2. Add Raw Materials
            </label>
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-[2] w-full">
                <Select
                  options={rawMaterials.map((rm) => ({
                    value: rm._id,
                    label: rm.name,
                    unitType: rm.unitType,
                  }))}
                  onChange={(opt: any) => {
                    setMaterialPick({
                      id: opt?.value || "",
                      quantity: 1,
                      unit: opt ? getDefaultUnitForType(opt.unitType) : "",
                    });
                  }}
                  value={
                    rawMaterials.find((m) => m._id === materialPick.id)
                      ? {
                          value: materialPick.id,
                          label: rawMaterials.find(
                            (m) => m._id === materialPick.id,
                          )?.name,
                        }
                      : null
                  }
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  placeholder="Select Material"
                />
              </div>
              <div className="flex gap-3 w-full md:w-auto">
                <div className="w-24 shrink-0">
                  <input
                    type="number"
                    min={1}
                    onWheel={handleWheelPrevent}
                    value={materialPick.quantity}
                    onChange={(e) =>
                      setMaterialPick((p) => ({
                        ...p,
                        quantity: +e.target.value,
                      }))
                    }
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg h-[42px] outline-none"
                  />
                </div>
                <div className="flex-1 min-w-[160px]">
                  <Select
                    options={
                      materialPick.id
                        ? getUnitsByType(
                            rawMaterials.find((m) => m._id === materialPick.id)
                              ?.unitType || "",
                          )
                        : UNITS
                    }
                    value={
                      UNITS.find((u) => u.value === materialPick.unit) || null
                    }
                    onChange={(opt: any) =>
                      setMaterialPick((p) => ({ ...p, unit: opt?.value || "" }))
                    }
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    placeholder="Unit"
                  />
                </div>
              </div>
              <Button
                type={ButtonType.Submit}
                onClick={addMaterial}
                className="text-xs h-[42px] px-8 w-full md:w-auto shrink-0"
              >
                Add
              </Button>
            </div>
          </div>

          <div className="border border-gray-100 rounded-xl overflow-hidden flex-1 flex flex-col mt-4">
            <div className="bg-gray-50 px-5 py-3 border-b border-gray-200 flex justify-between">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Added List
              </span>
              <span className="text-[10px] font-bold text-gray-400 uppercase">
                {selectedItems.length} items
              </span>
            </div>
            <ul className="divide-y divide-gray-100 max-h-[350px] overflow-y-auto bg-white">
              {selectedItems.map((item, idx) => (
                <li
                  key={idx}
                  className="flex justify-between items-center p-5 hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <span className="text-base font-bold text-gray-900 block">
                      {item.name}
                    </span>
                  </div>

                  <div className="flex items-center gap-10 shrink-0 ml-4">
                    <span
                      className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                        item.type === "product"
                          ? "bg-blue-50 text-blue-600"
                          : "bg-green-50 text-green-600"
                      }`}
                    >
                      {item.type}
                    </span>

                    {/* Editable Quantity */}
                    <div className="min-w-[140px] text-right flex items-center justify-end">
                      <input
                        type="number"
                        value={item.quantity}
                        onWheel={handleWheelPrevent}
                        min={1}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setSelectedItems((prev) =>
                            prev.map((it, i) =>
                              i === idx ? { ...it, quantity: value } : it,
                            ),
                          );
                        }}
                        className="w-16 text-right text-base font-black text-gray-800 border-b border-gray-300 focus:outline-none focus:border-blue-500"
                      />

                      <span className="text-sm font-bold text-gray-500 ml-2">
                        {item.type === "product" ? "Units" : item.unitLabel}
                      </span>
                    </div>

                    <button
                      onClick={() =>
                        setSelectedItems((prev) =>
                          prev.filter((_, i) => i !== idx),
                        )
                      }
                      className="text-gray-300 hover:text-red-500 transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-6">
          <Button type={ButtonType.Cancel} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type={ButtonType.Submit}
            onClick={handleSubmit}
            className="px-12 bg-navyBlue-primary text-white"
          >
            Save BOM
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateBOM;
